import { useSyncExternalStore } from "react";
import useOnlineStatus from "./useOnlineStatus.js";
import { subscribeStreamBandwidth, getStreamBandwidth } from "../utils/streamBandwidth.js";

// The admin's download speed for the left column's Internet card. Nothing is downloaded just
// to measure it:
//   source "browser" - Chromium (Chrome, Edge, Opera): the browser's own estimate
//     (Network Information API, navigator.connection), worked out from the traffic it has
//     seen recently, with a "change" event when it moves.
//       downlink      - download speed estimate in Mbps (Chrome rounds it to 25 kbps and caps
//                       it at 10 Mbps, so any faster line reads 10)
//       rtt           - round-trip time estimate in ms
//       effectiveType - "slow-2g" | "2g" | "3g" | "4g"
//   source "stream"  - Firefox / Safari (no navigator.connection): measured from the live
//     stream preview's own video segments (utils/streamBandwidth.js) - so only while it plays.
//   source null       - Firefox / Safari with no stream playing: no reading.
//   { online, source, mbps, rtt, effectiveType }

const connection = () => (typeof navigator !== "undefined" ? navigator.connection : undefined);

function subscribe(onChange) {
  const conn = connection();
  conn?.addEventListener?.("change", onChange);
  return () => conn?.removeEventListener?.("change", onChange);
}

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// useSyncExternalStore needs the same object back while nothing changed.
let cached = null;
function getSnapshot() {
  const conn = connection();
  // Only a connection object that actually carries a speed counts (Firefox for Android has
  // navigator.connection with just a "type" - it falls back to the stream like desktop Firefox).
  const next = typeof conn?.downlink === "number"
    ? {
        supported: true,
        mbps: num(conn.downlink),
        rtt: num(conn.rtt),
        effectiveType: typeof conn.effectiveType === "string" ? conn.effectiveType : null,
      }
    : { supported: false, mbps: null, rtt: null, effectiveType: null };
  if (
    cached &&
    cached.supported === next.supported &&
    cached.mbps === next.mbps &&
    cached.rtt === next.rtt &&
    cached.effectiveType === next.effectiveType
  ) {
    return cached;
  }
  cached = next;
  return cached;
}

const SERVER_SNAPSHOT = { supported: false, mbps: null, rtt: null, effectiveType: null };
const getServerSnapshot = () => SERVER_SNAPSHOT;

const getNoStream = () => null;

export default function useNetworkSpeed() {
  const online = useOnlineStatus();
  const reading = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const stream = useSyncExternalStore(subscribeStreamBandwidth, getStreamBandwidth, getNoStream);
  if (reading.supported) {
    return { online, source: "browser", mbps: reading.mbps, rtt: reading.rtt, effectiveType: reading.effectiveType };
  }
  return { online, source: stream ? "stream" : null, mbps: stream?.mbps ?? null, rtt: null, effectiveType: null };
}
